import type { GetStaticProps, NextPage } from "next";
import Link from "next/link";
import Image from "next/legacy/image";
import { useTranslation } from "next-i18next/pages";
import SubpageShell from "components/common/SubpageShell";
import Testimonial from "components/home/Testimonial";
import {
  getCaseStudyPreviews,
  type CaseStudyPreview
} from "lib/cms/caseStudies";
import {
  CMS_REVALIDATE_SECONDS,
  serverSideCmsTranslations
} from "lib/cms/translations";
import styles from "../../styles/case-studies/CaseStudies.module.css";

interface CaseStudiesPageProps {
  studies: CaseStudyPreview[];
}

export const getStaticProps: GetStaticProps<CaseStudiesPageProps> = async ({
  locale
}) => {
  const pageLocale = locale ?? "de";

  return {
    props: {
      studies: await getCaseStudyPreviews(pageLocale),
      ...(await serverSideCmsTranslations(pageLocale, ["common", "case-studies"]))
    },
    revalidate: CMS_REVALIDATE_SECONDS
  };
};

const CaseStudiesPage: NextPage<CaseStudiesPageProps> = ({ studies }) => {
  const { t } = useTranslation("case-studies");

  return (
    <SubpageShell
      title={t("global.websiteTitle")}
      description={t("global.websiteDescription")}
      path="/case-studies"
      eyebrow={t("global.eyebrow")}
      heading={t("global.heading")}
      subtitle={t("global.subtitle")}
      doAnimation
    >
      <div className={styles.container}>
        <div className={styles.previewGrid}>
          {studies.map((study: CaseStudyPreview, index: number) => {
            return (
              <Link
                key={study.id}
                href={`/case-studies/${study.id}`}
                className={styles.previewCard}
              >
                <div className={styles.previewBody}>
                  <span className={styles.jumpMeta}>
                    <span className={styles.jumpIndex}>0{index + 1}</span>
                    <span className={styles.jumpKicker}>{study.industry}</span>
                    {
                      study.disclaimer &&
                      <span className={styles.exampleBadge}>{t("exampleBadge")}</span>
                    }
                  </span>
                  <h2 className={styles.previewTitle}>{study.title}</h2>
                  <p className={styles.previewSummary}>{study.summary}</p>
                  <span className={styles.previewLink}>
                    {t("readMore")} <span aria-hidden="true">→</span>
                  </span>
                </div>
                {
                  study.previewImage &&
                  <div className={styles.previewMedia}>
                    <Image
                      src={study.previewImage.src}
                      layout="fill"
                      objectFit="cover"
                      objectPosition="top left"
                      sizes="(max-width: 1023px) 100vw, 40rem"
                      alt={study.previewImage.alt}
                    />
                  </div>
                }
              </Link>
            );
          })}
        </div>

        <div className={styles.ctaPanel}>
          <h2 className={styles.ctaTitle}>{t("cta.title")}</h2>
          <p className={styles.ctaText}>{t("cta.text")}</p>
          <Link href="/contact" className={styles.ctaButton}>
            {t("cta.button")}
          </Link>
        </div>
      </div>

      <Testimonial doAnimation />
    </SubpageShell>
  );
};

export default CaseStudiesPage;
